/**
 * ColorPicker — zero-deps picker do CellStylePicker (Krok 7.2.3).
 *
 * Przycisk z aktualnym kolorem; klik otwiera dropdown z 12 predefiniowanymi
 * kolorami (paleta tailwind 500) + natywnym <input type="color"> na dole +
 * przyciskiem „Wyczyść" (ustawia value=undefined).
 *
 * Dropdown renderowany przez portal do <body> z position:fixed (wzorzec
 * IconPicker) — wewnątrz modali `absolute` był klipowany przez scroll
 * kontenera i przegrywał stacking context z innymi popupami.
 *
 * Predefiniowane:
 *  czerwony / pomarańczowy / żółty / zielony / niebieski / indigo / fioletowy /
 *  różowy / brązowy / szary / czarny / biały
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const PRESETS: { hex: string; label: string }[] = [
  { hex: '#ef4444', label: 'Czerwony' },
  { hex: '#f97316', label: 'Pomarańczowy' },
  { hex: '#eab308', label: 'Żółty' },
  { hex: '#22c55e', label: 'Zielony' },
  { hex: '#3b82f6', label: 'Niebieski' },
  { hex: '#6366f1', label: 'Indigo' },
  { hex: '#a855f7', label: 'Fioletowy' },
  { hex: '#ec4899', label: 'Różowy' },
  { hex: '#a16207', label: 'Brązowy' },
  { hex: '#6b7280', label: 'Szary' },
  { hex: '#000000', label: 'Czarny' },
  { hex: '#ffffff', label: 'Biały' },
];

/** Szerokość dropdownu (Tailwind w-56). */
const DROPDOWN_WIDTH = 224;

interface ColorPickerProps {
  value: string | undefined;
  onChange: (hex: string | undefined) => void;
  label?: string;
  allowClear?: boolean;
}

export function ColorPicker({
  value,
  onChange,
  label,
  allowClear = true,
}: ColorPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);

  function openDropdown() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const margin = 8;
      // Przytnij do viewportu, żeby popup nie wychodził poza prawą krawędź ekranu.
      const maxLeft = window.innerWidth - DROPDOWN_WIDTH - margin;
      const left = Math.max(margin, Math.min(rect.left, maxLeft));
      // Pionowo: pod triggerem; gdy brak miejsca w dół a w górę więcej — nad triggerem.
      const desired = 160;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      if (spaceBelow >= desired || spaceBelow >= spaceAbove) {
        setPos({ top: rect.bottom + 4, left });
      } else {
        setPos({ bottom: window.innerHeight - rect.top + 4, left });
      }
    }
    setOpen(true);
  }

  // Zamykaj na klik poza i na scroll (popup jest fixed — przy scrollu odjechałby od triggera).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    // Esc zamyka tylko paletę: faza przechwytywania + `preventDefault`, więc okno nakładkowe
    // (`Overlay`), w którym stoi picker, widzi Esc jako obsłużony i się nie zamyka.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const dropdown =
    open && pos ? (
      <div
        ref={dropdownRef}
        role="dialog"
        aria-label="Wybór koloru"
        style={{
          position: 'fixed',
          top: pos.top,
          bottom: pos.bottom,
          left: pos.left,
          width: DROPDOWN_WIDTH,
          // Ponad okna (`Modal`/`Overlay` mają z-50) — paleta otwarta z wnętrza okna
          // (np. edytor reguły formatowania) nie może zostać pod jego przesłoną.
          zIndex: 10001,
        }}
        className="rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800"
      >
        {/* Paleta predefiniowanych */}
        <div className="grid grid-cols-6 gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.hex}
              type="button"
              onClick={() => {
                onChange(p.hex);
                setOpen(false);
              }}
              title={p.label}
              aria-label={p.label}
              className={`h-6 w-6 rounded border ${
                value === p.hex ? 'ring-2 ring-accent-500' : 'border-gray-300'
              }`}
              style={{ backgroundColor: p.hex }}
            />
          ))}
        </div>

        {/* Native input type=color */}
        <div className="mt-2 flex items-center gap-2 border-t border-gray-200 pt-2 dark:border-gray-700">
          <input
            type="color"
            value={value ?? '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="h-7 w-12 cursor-pointer rounded border border-gray-300"
            aria-label="Wybierz własny kolor"
          />
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (/^#[0-9A-Fa-f]{6}$/.test(v)) onChange(v);
              else if (v === '') onChange(undefined);
            }}
            placeholder="#rrggbb"
            className="flex-1 rounded border border-gray-300 px-2 py-0.5 font-mono text-xs dark:border-gray-600 dark:bg-gray-900"
            aria-label="Hex kolor"
          />
          {allowClear && value && (
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
              className="rounded p-1 text-text-tertiary hover:bg-bg-secondary hover:text-text-primary"
              title="Wyczyść"
              aria-label="Wyczyść kolor"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        className="flex items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-2 py-1 text-sm hover:bg-bg-secondary"
        aria-label={label ?? 'Wybierz kolor'}
        aria-expanded={open}
        title={label}
      >
        <span
          className="inline-block h-4 w-4 rounded border border-gray-300"
          style={{
            backgroundColor: value || 'transparent',
            backgroundImage: value
              ? undefined
              : 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)',
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 4px 4px',
          }}
          aria-hidden="true"
        />
        <span className="text-xs text-text-tertiary">{value ?? 'brak'}</span>
      </button>

      {createPortal(dropdown, document.body)}
    </div>
  );
}
